import { Outlet, Link, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { LuFuel } from 'react-icons/lu';
import { Avatar } from '@/shared/ui/chakra/avatar';
import { LogoMark } from '@/shared/ui/Logo';
import { ColorModeButton, useColorModeValue } from '@/shared/ui/chakra/color-mode';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/shared/ui/chakra/menu';
import { useAuthStore } from '@/shared/lib/auth/store';
import { logout } from '@/features/auth/api/logout';

/* Provider "client cabinet" layout — the same shell language as the admin
   AppShell (sidebar + 64px top bar, shared Chakra tokens / theme toggle),
   trimmed to the cabinet's single section. Sub-pages render into <Outlet/>.
   Routing keeps this shell provider-only (see router `clientRoute`). */

export function ClientShell() {
  const { t } = useTranslation();
  const reset = useAuthStore((s) => s.reset);
  const router = useRouter();
  const logoTone = useColorModeValue<'brand' | 'onDark'>('brand', 'onDark');

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      reset();
      await router.navigate({ to: '/login' });
    }
  };

  return (
    <Flex h="100vh">
      <Box
        as="aside"
        aria-label={t('client_cabinet.brand')}
        w="260px"
        flexShrink={0}
        borderRightWidth="1px"
        borderColor="border"
        bg="bg.panel"
        p="4"
        display="flex"
        flexDirection="column"
        gap="6"
        overflowY="auto"
      >
        <Flex align="center" gap="2.5" px="2">
          <LogoMark size={26} tone={logoTone} />
          <Heading size="md" color="colorPalette.fg">
            {t('client_cabinet.brand')}
          </Heading>
        </Flex>
        <Stack as="nav" gap="1">
          <Link to="/client" style={{ textDecoration: 'none' }} activeOptions={{ exact: true }}>
            {({ isActive }) => (
              <Flex
                align="center"
                gap="3"
                px="3"
                py="2"
                rounded="md"
                fontSize="sm"
                fontWeight={isActive ? 'semibold' : 'normal'}
                color={isActive ? 'colorPalette.fg' : 'fg'}
                bg={isActive ? 'colorPalette.subtle' : 'transparent'}
                transition="background 0.12s"
                _hover={{ bg: isActive ? 'colorPalette.subtle' : 'bg.muted' }}
              >
                <LuFuel size={18} />
                <Text>{t('client_cabinet.title')}</Text>
              </Flex>
            )}
          </Link>
        </Stack>
      </Box>

      <Flex direction="column" flex="1" minH="0">
        <Flex
          as="header"
          align="center"
          justify="flex-end"
          gap="3"
          px="6"
          h="64px"
          flexShrink={0}
          borderBottomWidth="1px"
          borderColor="border"
          bg="bg.panel"
        >
          <ColorModeButton />
          <MenuRoot>
            <MenuTrigger asChild>
              <Box as="button" aria-label={t('common.account')} cursor="pointer" rounded="full">
                <Avatar size="sm" name={t('client_cabinet.brand')} />
              </Box>
            </MenuTrigger>
            <MenuContent>
              <MenuItem value="sign-out" onClick={handleSignOut}>
                {t('common.sign_out')}
              </MenuItem>
            </MenuContent>
          </MenuRoot>
        </Flex>

        <Box as="main" flex="1" overflowY="auto" p="6" bg="bg.subtle">
          <Outlet />
        </Box>
      </Flex>
    </Flex>
  );
}
